import { z } from "zod/v4-mini";
import type {
	AccessToken,
	AuthUser,
	IdToken,
	RefreshToken,
} from "./auth-user.ts";

function decodeJwt(token: string) {
	const [, payload] = token.split(".");
	return JSON.parse(atob(payload));
}

const idTokenSchema = z.object({
	sub: z.string(),
	email: z.string(),
	given_name: z.optional(z.string()),
	middle_name: z.optional(z.string()),
	family_name: z.optional(z.string()),
	birthdate: z.optional(z.string()),
	email_verified: z.boolean(),
	"custom:origin_site": z.string(),
	"custom:current_site": z.string(),
	"custom:country": z.optional(z.string()),
	"custom:ip_address": z.optional(z.string()),
	"custom:ip_address_country": z.optional(z.string()),
	"custom:art_product_creator": z.optional(z.string()),
	"custom:how_heard": z.optional(z.string()),
});

function decodeIdToken(token: string) {
	const decoded = decodeJwt(token);
	return idTokenSchema.parse(decoded);
}

const accessTokenSchema = z.object({
	token_use: z.literal("access"),
	exp: z.int().check(z.positive()),
});

function decodeAccessToken(token: string) {
	const decoded = decodeJwt(token);
	return accessTokenSchema.parse(decoded);
}

type TokensToAuthUserOptions = {
	readonly accessToken: AccessToken;
	readonly idToken: IdToken;
	readonly refreshToken: RefreshToken;
};

function tokensToAuthUser({
	accessToken,
	idToken,
	refreshToken,
}: TokensToAuthUserOptions): AuthUser | undefined {
	const parsedIdPayload = idTokenSchema.parse(decodeIdToken(idToken.idToken));
	return {
		id: parsedIdPayload.sub,
		emailAddress: parsedIdPayload.email,
		emailVerified: parsedIdPayload["email_verified"],
		accessToken,
		idToken,
		refreshToken,

		firstName: parsedIdPayload.given_name,
		middleName: parsedIdPayload.middle_name,
		lastName: parsedIdPayload.family_name,
		howHeard: parsedIdPayload["custom:how_heard"],
		country: parsedIdPayload["custom:country"],
		originSite: parsedIdPayload["custom:origin_site"],
		currentSite: parsedIdPayload["custom:current_site"],
		ipAddress: parsedIdPayload["custom:ip_address"],
		ipAddressCountry: parsedIdPayload["custom:ip_address_country"],
		birthdate: parsedIdPayload.birthdate,
		artProductCreator: parsedIdPayload["custom:art_product_creator"] === "1",
	};
}

export { tokensToAuthUser, decodeAccessToken };
